/**
 * Demand Research Page
 *
 * Provides category-level demand vs readiness analysis.
 * Visualizes categories in a quadrant chart and table view.
 *
 * Quadrants:
 * - TOP_PRIORITY: High demand, low readiness - create content now
 * - PROTECT: High demand, high readiness - maintain quality
 * - DEVELOP: Medium demand/readiness - balanced growth
 * - OPTIMIZE: Low demand, high readiness - reduce effort
 * - DEPRIORITIZE: Low demand, low readiness - lowest priority
 *
 * Date: 2026-01-26
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Target,
  Shield,
  TrendingUp,
  Settings,
  Search,
  AlertCircle,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  RefreshCw,
  Download,
  Globe,
  DollarSign,
  Package,
  BarChart3,
  ChevronRight,
  X,
  Info,
  CheckCircle2,
  XCircle,
  Hash,
  Languages,
  MapPin,
} from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useSite } from '@/contexts/SiteContext';
import {
  marketsService,
  CategoryDemandReadiness,
  DemandReadinessResponse,
  MarketOpportunitySummary,
  KeywordEnrichmentStatus,
  CategoryDetailResponse,
} from '@/services/marketsService';
import { Link } from 'react-router-dom';
import { useToast } from '@/components/ui/use-toast';

// Quadrant colors and labels
const QUADRANT_CONFIG = {
  TOP_PRIORITY: {
    label: 'Top Priority',
    color: 'bg-red-500',
    textColor: 'text-red-700',
    bgLight: 'bg-red-50',
    icon: Target,
    description: 'High demand, low readiness - create content now',
  },
  PROTECT: {
    label: 'Protect',
    color: 'bg-green-500',
    textColor: 'text-green-700',
    bgLight: 'bg-green-50',
    icon: Shield,
    description: 'High demand, high readiness - maintain quality',
  },
  DEVELOP: {
    label: 'Develop',
    color: 'bg-blue-500',
    textColor: 'text-blue-700',
    bgLight: 'bg-blue-50',
    icon: TrendingUp,
    description: 'Medium demand/readiness - balanced growth',
  },
  OPTIMIZE: {
    label: 'Optimize',
    color: 'bg-yellow-500',
    textColor: 'text-yellow-700',
    bgLight: 'bg-yellow-50',
    icon: Settings,
    description: 'Low demand, high readiness - optimize efficiency',
  },
  DEPRIORITIZE: {
    label: 'Deprioritize',
    color: 'bg-gray-400',
    textColor: 'text-gray-600',
    bgLight: 'bg-gray-50',
    icon: Minus,
    description: 'Low demand, low readiness - lowest priority',
  },
};

function formatCurrency(value: number, currency = 'EUR'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function formatNumber(value: number): string {
  if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
  return value.toString();
}

// Quadrant Chart Component
function QuadrantChart({
  data,
  thresholds,
  currency,
  onCategoryClick,
}: {
  data: DemandReadinessResponse;
  thresholds: { highDemand: number; lowDemand: number; highReadiness: number; lowReadiness: number };
  currency: string;
  onCategoryClick?: (category: string) => void;
}) {
  const [hoveredCategory, setHoveredCategory] = useState<string | null>(null);

  // Calculate chart dimensions and scales
  const maxSearchValue = Math.max(...data.categories.map(c => c.searchValue), thresholds.highDemand * 2);
  const chartWidth = 600;
  const chartHeight = 400;
  const padding = { top: 40, right: 40, bottom: 60, left: 80 };

  const xScale = (readiness: number) =>
    padding.left + ((chartWidth - padding.left - padding.right) * readiness) / 100;

  const yScale = (value: number) =>
    chartHeight - padding.bottom - ((chartHeight - padding.top - padding.bottom) * Math.min(value, maxSearchValue)) / maxSearchValue;

  // Threshold lines
  const highDemandY = yScale(thresholds.highDemand);
  const lowDemandY = yScale(thresholds.lowDemand);
  const lowReadinessX = xScale(thresholds.lowReadiness);
  const highReadinessX = xScale(thresholds.highReadiness);

  return (
    <div className="relative">
      <svg width={chartWidth} height={chartHeight} className="border rounded-lg bg-white">
        {/* Quadrant backgrounds */}
        {/* Top Priority (top-left) */}
        <rect
          x={padding.left}
          y={padding.top}
          width={lowReadinessX - padding.left}
          height={highDemandY - padding.top}
          fill="rgba(239, 68, 68, 0.1)"
        />
        {/* Protect (top-right) */}
        <rect
          x={highReadinessX}
          y={padding.top}
          width={chartWidth - padding.right - highReadinessX}
          height={highDemandY - padding.top}
          fill="rgba(34, 197, 94, 0.1)"
        />
        {/* Deprioritize (bottom-left) */}
        <rect
          x={padding.left}
          y={lowDemandY}
          width={lowReadinessX - padding.left}
          height={chartHeight - padding.bottom - lowDemandY}
          fill="rgba(156, 163, 175, 0.1)"
        />
        {/* Optimize (bottom-right) */}
        <rect
          x={highReadinessX}
          y={lowDemandY}
          width={chartWidth - padding.right - highReadinessX}
          height={chartHeight - padding.bottom - lowDemandY}
          fill="rgba(234, 179, 8, 0.1)"
        />

        {/* Threshold lines */}
        <line
          x1={padding.left}
          y1={highDemandY}
          x2={chartWidth - padding.right}
          y2={highDemandY}
          stroke="#dc2626"
          strokeWidth="1"
          strokeDasharray="4,4"
          opacity="0.5"
        />
        <line
          x1={padding.left}
          y1={lowDemandY}
          x2={chartWidth - padding.right}
          y2={lowDemandY}
          stroke="#9ca3af"
          strokeWidth="1"
          strokeDasharray="4,4"
          opacity="0.5"
        />
        <line
          x1={lowReadinessX}
          y1={padding.top}
          x2={lowReadinessX}
          y2={chartHeight - padding.bottom}
          stroke="#9ca3af"
          strokeWidth="1"
          strokeDasharray="4,4"
          opacity="0.5"
        />
        <line
          x1={highReadinessX}
          y1={padding.top}
          x2={highReadinessX}
          y2={chartHeight - padding.bottom}
          stroke="#22c55e"
          strokeWidth="1"
          strokeDasharray="4,4"
          opacity="0.5"
        />

        {/* Axes */}
        <line
          x1={padding.left}
          y1={chartHeight - padding.bottom}
          x2={chartWidth - padding.right}
          y2={chartHeight - padding.bottom}
          stroke="#e5e7eb"
          strokeWidth="2"
        />
        <line
          x1={padding.left}
          y1={padding.top}
          x2={padding.left}
          y2={chartHeight - padding.bottom}
          stroke="#e5e7eb"
          strokeWidth="2"
        />

        {/* Axis labels */}
        <text
          x={chartWidth / 2}
          y={chartHeight - 15}
          textAnchor="middle"
          className="text-xs fill-gray-500"
        >
          Content Readiness (%)
        </text>
        <text
          x={15}
          y={chartHeight / 2}
          textAnchor="middle"
          transform={`rotate(-90, 15, ${chartHeight / 2})`}
          className="text-xs fill-gray-500"
        >
          Search Value ({currency}/mo)
        </text>

        {/* X-axis ticks */}
        {[0, 25, 50, 75, 100].map(tick => (
          <g key={`x-${tick}`}>
            <line
              x1={xScale(tick)}
              y1={chartHeight - padding.bottom}
              x2={xScale(tick)}
              y2={chartHeight - padding.bottom + 5}
              stroke="#9ca3af"
            />
            <text
              x={xScale(tick)}
              y={chartHeight - padding.bottom + 20}
              textAnchor="middle"
              className="text-xs fill-gray-500"
            >
              {tick}%
            </text>
          </g>
        ))}

        {/* Y-axis ticks */}
        {[0, thresholds.lowDemand, thresholds.highDemand, maxSearchValue].map(tick => (
          <g key={`y-${tick}`}>
            <line
              x1={padding.left - 5}
              y1={yScale(tick)}
              x2={padding.left}
              y2={yScale(tick)}
              stroke="#9ca3af"
            />
            <text
              x={padding.left - 10}
              y={yScale(tick) + 4}
              textAnchor="end"
              className="text-xs fill-gray-500"
            >
              {formatNumber(tick)}
            </text>
          </g>
        ))}

        {/* Data points */}
        {data.categories.map((cat) => {
          const x = xScale(cat.readinessPct);
          const y = yScale(cat.searchValue);
          const isHovered = hoveredCategory === cat.category;
          const config = QUADRANT_CONFIG[cat.quadrant];
          const radius = Math.max(4, Math.min(12, Math.sqrt(cat.totalProducts) * 1.5));

          // Tooltip positioning - flip to left if near right edge
          const tooltipWidth = 180;
          const tooltipHeight = 70;
          const flipToLeft = x + 15 + tooltipWidth > chartWidth - padding.right;
          const tooltipX = flipToLeft ? x - 15 - tooltipWidth : x + 15;
          const textX = tooltipX + 10;

          // Flip tooltip down if near top edge
          const flipToBottom = y - 45 < padding.top;
          const tooltipY = flipToBottom ? y + 15 : y - 45;
          const textYBase = flipToBottom ? y + 35 : y - 25;

          return (
            <g
              key={cat.category}
              onMouseEnter={() => setHoveredCategory(cat.category)}
              onMouseLeave={() => setHoveredCategory(null)}
              onClick={() => onCategoryClick?.(cat.category)}
              style={{ cursor: 'pointer' }}
            >
              <circle
                cx={x}
                cy={y}
                r={isHovered ? radius + 2 : radius}
                className={`${config.color} transition-all duration-200`}
                opacity={isHovered ? 1 : 0.7}
              />
              {isHovered && (
                <g>
                  <rect
                    x={tooltipX}
                    y={tooltipY}
                    width={tooltipWidth}
                    height={tooltipHeight}
                    rx="4"
                    fill="white"
                    stroke="#e5e7eb"
                    strokeWidth="1"
                    style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))' }}
                  />
                  <text x={textX} y={textYBase} className="text-xs font-medium fill-gray-900">
                    {cat.category.length > 25 ? cat.category.slice(0, 25) + '...' : cat.category}
                  </text>
                  <text x={textX} y={textYBase + 17} className="text-xs fill-gray-500">
                    Value: {formatCurrency(cat.searchValue, currency)}
                  </text>
                  <text x={textX} y={textYBase + 33} className="text-xs fill-gray-500">
                    Readiness: {cat.readinessPct.toFixed(1)}%
                  </text>
                  <text x={textX} y={textYBase + 48} className="text-xs fill-gray-500">
                    Products: {cat.totalProducts}
                  </text>
                </g>
              )}
            </g>
          );
        })}

        {/* Quadrant labels */}
        <text x={padding.left + 10} y={padding.top + 20} className="text-xs font-medium fill-red-600">
          TOP PRIORITY
        </text>
        <text x={chartWidth - padding.right - 60} y={padding.top + 20} className="text-xs font-medium fill-green-600">
          PROTECT
        </text>
        <text x={padding.left + 10} y={chartHeight - padding.bottom - 10} className="text-xs font-medium fill-gray-500">
          DEPRIORITIZE
        </text>
        <text x={chartWidth - padding.right - 60} y={chartHeight - padding.bottom - 10} className="text-xs font-medium fill-yellow-600">
          OPTIMIZE
        </text>
      </svg>
    </div>
  );
}

// Summary Card Component
function SummaryCard({
  title,
  value,
  subtitle,
  secondaryValue,
  secondaryLabel,
  icon: Icon,
  trend,
  tooltip,
}: {
  title: string;
  value: string;
  subtitle?: string;
  secondaryValue?: string;
  secondaryLabel?: string;
  icon: React.ElementType;
  trend?: 'up' | 'down' | 'neutral';
  tooltip?: string;
}) {
  const content = (
    <Card className={tooltip ? 'cursor-help' : ''}>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground flex items-center gap-1">
              {title}
              {tooltip && <Info className="h-3 w-3 text-muted-foreground/60" />}
            </p>
            <p className="text-2xl font-bold">{value}</p>
            {secondaryValue && secondaryLabel && (
              <p className="text-sm font-medium text-green-600 mt-0.5">{secondaryValue} <span className="text-xs font-normal text-muted-foreground">{secondaryLabel}</span></p>
            )}
            {subtitle && (
              <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
            )}
          </div>
          <div className={`p-3 rounded-lg ${
            trend === 'up' ? 'bg-green-100' :
            trend === 'down' ? 'bg-red-100' :
            'bg-blue-100'
          }`}>
            <Icon className={`h-6 w-6 ${
              trend === 'up' ? 'text-green-600' :
              trend === 'down' ? 'text-red-600' :
              'text-blue-600'
            }`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (tooltip) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          {content}
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-xs text-sm">
          <p>{tooltip}</p>
        </TooltipContent>
      </Tooltip>
    );
  }

  return content;
}

export default function DemandResearch() {
  const { selectedSite } = useSite();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<DemandReadinessResponse | null>(null);
  const [summary, setSummary] = useState<MarketOpportunitySummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [quadrantFilter, setQuadrantFilter] = useState<string>('all');
  const [keywordStatus, setKeywordStatus] = useState<KeywordEnrichmentStatus | null>(null);
  const [isEnriching, setIsEnriching] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [categoryDetail, setCategoryDetail] = useState<CategoryDetailResponse | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  useEffect(() => {
    if (selectedSite?.id) {
      loadData();
      loadKeywordStatus();
    }
  }, [selectedSite?.id]);

  const loadData = async () => {
    if (!selectedSite?.id) return;
    setLoading(true);
    setError(null);
    setData(null);
    setSummary(null);
    try {
      const [demandData, summaryData] = await Promise.all([
        marketsService.getCategoryDemandReadiness(selectedSite.id),
        marketsService.getMarketOpportunitySummary(selectedSite.id),
      ]);
      setData(demandData);
      setSummary(summaryData);
    } catch (err) {
      console.error('Failed to load demand data:', err);
      setError('Failed to load demand research data. Make sure you have configured markets.');
    } finally {
      setLoading(false);
    }
  };

  const loadKeywordStatus = async () => {
    if (!selectedSite?.id) return;
    try {
      const status = await marketsService.getKeywordEnrichmentStatus(selectedSite.id);
      setKeywordStatus(status);
    } catch (err) {
      console.error('Failed to load keyword status:', err);
    }
  };

  const loadCategoryDetail = async (category: string) => {
    if (!selectedSite?.id) return;
    setSelectedCategory(category);
    setLoadingDetail(true);
    setCategoryDetail(null);
    try {
      const detail = await marketsService.getCategoryDetail(selectedSite.id, category);
      setCategoryDetail(detail);
    } catch (err) {
      console.error('Failed to load category detail:', err);
      toast({
        title: 'Failed to load category detail',
        description: 'Unable to fetch keyword and market data for this category.',
        variant: 'destructive',
      });
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleTriggerEnrichment = async () => {
    if (!selectedSite?.id) return;
    setIsEnriching(true);
    try {
      const result = await marketsService.triggerKeywordEnrichment(selectedSite.id);
      toast({
        title: 'Keyword enrichment started',
        description: `Processing markets: ${result.markets}. This may take several minutes.`,
      });
      // Poll for status updates
      const pollInterval = setInterval(async () => {
        const status = await marketsService.getKeywordEnrichmentStatus(selectedSite.id);
        setKeywordStatus(status);
        if (status.coverage > (keywordStatus?.coverage || 0)) {
          clearInterval(pollInterval);
          setIsEnriching(false);
          loadData(); // Refresh the main data
          toast({
            title: 'Keyword enrichment complete',
            description: `${status.enriched} products now have keyword data (${status.coverage}% coverage).`,
          });
        }
      }, 10000);
      // Stop polling after 5 minutes
      setTimeout(() => {
        clearInterval(pollInterval);
        setIsEnriching(false);
        loadKeywordStatus();
        loadData();
      }, 300000);
    } catch (err) {
      console.error('Failed to trigger enrichment:', err);
      toast({
        title: 'Enrichment failed',
        description: 'Failed to start keyword enrichment. Please try again.',
        variant: 'destructive',
      });
      setIsEnriching(false);
    }
  };

  const filteredCategories = useMemo(() => {
    if (!data) return [];
    return data.categories.filter(cat => {
      const matchesSearch = cat.category.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesQuadrant = quadrantFilter === 'all' || cat.quadrant === quadrantFilter;
      return matchesSearch && matchesQuadrant;
    });
  }, [data, searchTerm, quadrantFilter]);

  const exportToCsv = () => {
    if (!filteredCategories.length) return;
    const headers = [
      'Category',
      'Quadrant',
      'Search Value',
      'Readiness %',
      'Opportunity Gap',
      'Total Products',
      'Products with Content',
      'Avg CPC',
      'Total Searches',
    ];
    const rows = filteredCategories.map(cat => [
      cat.category,
      cat.quadrant,
      cat.searchValue,
      cat.readinessPct,
      cat.opportunityGap,
      cat.totalProducts,
      cat.productsWithContent,
      cat.avgCpc,
      cat.totalSearches,
    ]);
    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `demand-research-${selectedSite?.name || 'site'}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!selectedSite) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-muted-foreground">
              <AlertCircle className="h-5 w-5" />
              <span>Please select a site to view demand research.</span>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const currency = data?.market?.currency || 'EUR';

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Demand Research</h1>
          <p className="text-muted-foreground">
            Category demand vs content readiness analysis
          </p>
        </div>
        <div className="flex items-center gap-4">
          {/* Keyword Data Coverage Indicator */}
          {keywordStatus && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground border rounded-md px-3 py-1.5">
              <DollarSign className="h-4 w-4" />
              <span>
                Keyword Data: {keywordStatus.coverage}%
                ({keywordStatus.enriched.toLocaleString()}/{keywordStatus.total.toLocaleString()})
              </span>
            </div>
          )}
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={loadData} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              variant="outline"
              onClick={handleTriggerEnrichment}
              disabled={isEnriching}
              title="Enrich products with CPC and search volume data from Google Ads"
            >
              {isEnriching ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Enriching...
                </>
              ) : (
                <>
                  <DollarSign className="h-4 w-4 mr-2" />
                  Enrich Keywords
                </>
              )}
            </Button>
            <Button variant="outline" onClick={exportToCsv} disabled={!filteredCategories.length}>
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
            <Button asChild>
              <Link to="/settings/markets">
                <Globe className="h-4 w-4 mr-2" />
                Configure Markets
              </Link>
            </Button>
          </div>
        </div>
      </div>

      {/* Keyword Data Quality Alert */}
      {keywordStatus && keywordStatus.coverage < 10 && (
        <Card className="border-yellow-500 bg-yellow-50 dark:bg-yellow-950/20">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-yellow-700 dark:text-yellow-500">
                <AlertCircle className="h-5 w-5" />
                <div>
                  <p className="font-medium">Limited keyword data available</p>
                  <p className="text-sm text-yellow-600 dark:text-yellow-400">
                    Only {keywordStatus.coverage}% of products have search volume data.
                    Demand values may not be accurate. Run keyword enrichment to improve data quality.
                  </p>
                </div>
              </div>
              <Button
                onClick={handleTriggerEnrichment}
                disabled={isEnriching}
                className="bg-yellow-600 hover:bg-yellow-700"
              >
                {isEnriching ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Enriching...
                  </>
                ) : (
                  <>
                    <DollarSign className="h-4 w-4 mr-2" />
                    Enrich Keywords
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {error && (
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              <span>{error}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="grid grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i}>
              <CardContent className="pt-6">
                <div className="h-16 bg-muted animate-pulse rounded" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : summary && (
        <>
          {/* Summary Cards */}
          <TooltipProvider delayDuration={200}>
            <div className="grid grid-cols-4 gap-4">
              <SummaryCard
                title="Total Market Value"
                value={formatCurrency(summary.summary.totalMarketValue, currency)}
                secondaryValue={formatCurrency(summary.summary.totalMarketValue * 0.02, currency)}
                secondaryLabel="est. monthly revenue"
                subtitle="Monthly search value"
                icon={DollarSign}
                tooltip="Total Market Value = Sum of (Monthly Searches × CPC) for all unique keywords. Revenue estimate assumes 5% organic CTR, 2% conversion, and AOV/CPC ratio of 20 (conservative)."
              />
              <SummaryCard
                title="Captured Value"
                value={formatCurrency(summary.summary.capturedValue, currency)}
                subtitle={`${summary.summary.captureRate}% capture rate`}
                icon={Shield}
                trend="up"
                tooltip="Captured Value = Search value from categories where you have content (readiness > 0%). This represents keywords you're positioned to rank for with existing product content."
              />
              <SummaryCard
                title="Opportunity Gap"
                value={formatCurrency(summary.summary.opportunityGap, currency)}
                subtitle="Uncaptured potential"
                icon={Target}
                trend="down"
                tooltip="Opportunity Gap = Total Market Value - Captured Value. This is the search value you're missing because categories lack content coverage. Priority: create content for high-value uncaptured categories."
              />
              <SummaryCard
                title="Categories"
                value={summary.summary.totalCategories.toString()}
                subtitle={`${summary.summary.categoriesWithData} with keyword data`}
                icon={Package}
                tooltip="Total product categories in your catalog. Categories with keyword data have Google Ads metrics (CPC and monthly searches) from the enrichment process."
              />
            </div>
          </TooltipProvider>

          {/* Quadrant Distribution */}
          <div className="grid grid-cols-5 gap-4">
            {Object.entries(QUADRANT_CONFIG).map(([key, config]) => {
              const count = summary.quadrantCounts[key as keyof typeof summary.quadrantCounts];
              const Icon = config.icon;
              return (
                <Card key={key} className={`${config.bgLight} border-0`}>
                  <CardContent className="pt-4 pb-4">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${config.color} bg-opacity-20`}>
                        <Icon className={`h-4 w-4 ${config.textColor}`} />
                      </div>
                      <div>
                        <p className={`text-xs ${config.textColor}`}>{config.label}</p>
                        <p className={`text-xl font-bold ${config.textColor}`}>{count}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </>
      )}

      {/* Main Content */}
      {data && (
        <Tabs defaultValue="chart" className="space-y-4">
          <TabsList>
            <TabsTrigger value="chart">
              <BarChart3 className="h-4 w-4 mr-2" />
              Quadrant Chart
            </TabsTrigger>
            <TabsTrigger value="table">
              <Package className="h-4 w-4 mr-2" />
              Category Table
            </TabsTrigger>
          </TabsList>

          <TabsContent value="chart">
            <Card>
              <CardHeader>
                <CardTitle>Demand vs Readiness Quadrant</CardTitle>
                <CardDescription>
                  Categories plotted by search value (demand) and content coverage (readiness).
                  Bubble size represents product count. Click a category for detailed breakdown.
                </CardDescription>
              </CardHeader>
              <CardContent className="flex justify-center">
                <QuadrantChart
                  data={data}
                  thresholds={data.thresholds}
                  currency={currency}
                  onCategoryClick={loadCategoryDetail}
                />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="table">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Category Analysis</CardTitle>
                    <CardDescription>
                      All categories with demand and readiness metrics. Click a row for details.
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Search categories..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-9 w-64"
                      />
                    </div>
                    <Select value={quadrantFilter} onValueChange={setQuadrantFilter}>
                      <SelectTrigger className="w-40">
                        <SelectValue placeholder="Filter quadrant" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Quadrants</SelectItem>
                        <SelectItem value="TOP_PRIORITY">Top Priority</SelectItem>
                        <SelectItem value="PROTECT">Protect</SelectItem>
                        <SelectItem value="DEVELOP">Develop</SelectItem>
                        <SelectItem value="OPTIMIZE">Optimize</SelectItem>
                        <SelectItem value="DEPRIORITIZE">Deprioritize</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Category</TableHead>
                      <TableHead>Quadrant</TableHead>
                      <TableHead className="text-right">Search Value</TableHead>
                      <TableHead className="text-right">Readiness</TableHead>
                      <TableHead className="text-right">Opportunity Gap</TableHead>
                      <TableHead className="text-right">Products</TableHead>
                      <TableHead className="text-right">With Content</TableHead>
                      <TableHead className="text-right">Avg CPC</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredCategories.map((cat) => {
                      const config = QUADRANT_CONFIG[cat.quadrant];
                      return (
                        <TableRow
                          key={cat.category}
                          className="cursor-pointer hover:bg-muted/50"
                          onClick={() => loadCategoryDetail(cat.category)}
                        >
                          <TableCell className="font-medium max-w-[200px] truncate" title={cat.category}>
                            <div className="flex items-center gap-2">
                              <span>{cat.category}</span>
                              <ChevronRight className="h-4 w-4 text-muted-foreground" />
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={`${config.color} text-white`}>
                              {config.label}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            {formatCurrency(cat.searchValue, currency)}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                                <div
                                  className={`h-full ${
                                    cat.readinessPct >= 70 ? 'bg-green-500' :
                                    cat.readinessPct >= 30 ? 'bg-blue-500' :
                                    'bg-red-500'
                                  }`}
                                  style={{ width: `${cat.readinessPct}%` }}
                                />
                              </div>
                              <span className="text-sm">{cat.readinessPct.toFixed(1)}%</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-right font-mono text-red-600">
                            {formatCurrency(cat.opportunityGap, currency)}
                          </TableCell>
                          <TableCell className="text-right">{cat.totalProducts}</TableCell>
                          <TableCell className="text-right">
                            {cat.productsWithContent} ({Math.round((cat.productsWithContent / cat.totalProducts) * 100)}%)
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            {cat.avgCpc.toFixed(2)} {currency}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {filteredCategories.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                          No categories found matching your filters.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}

      {/* Category Detail Panel */}
      <Sheet open={!!selectedCategory} onOpenChange={(open) => !open && setSelectedCategory(null)}>
        <SheetContent className="w-[600px] sm:max-w-[600px] overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              {selectedCategory}
            </SheetTitle>
            <SheetDescription>
              Deep dive into keywords, markets, and data computation
            </SheetDescription>
          </SheetHeader>

          {loadingDetail && (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          )}

          {categoryDetail && !loadingDetail && (
            <div className="mt-6 space-y-6">
              {/* Summary Stats */}
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-lg border p-3">
                  <p className="text-xs text-muted-foreground">Products</p>
                  <p className="text-xl font-bold">{categoryDetail.summary.totalProducts}</p>
                  <p className="text-xs text-muted-foreground">
                    {categoryDetail.summary.productsWithContent} with content
                  </p>
                </div>
                <div className="rounded-lg border p-3">
                  <p className="text-xs text-muted-foreground">Search Value</p>
                  <p className="text-xl font-bold">
                    {formatCurrency(categoryDetail.summary.totalSearchValue, categoryDetail.market.currency)}
                  </p>
                  <p className="text-xs text-muted-foreground">/month</p>
                </div>
                <div className="rounded-lg border p-3">
                  <p className="text-xs text-muted-foreground">Readiness</p>
                  <p className="text-xl font-bold">{categoryDetail.summary.readinessPct}%</p>
                  <Progress value={categoryDetail.summary.readinessPct} className="h-1 mt-1" />
                </div>
              </div>

              <Separator />

              {/* Keywords Section */}
              <div>
                <h4 className="font-semibold flex items-center gap-2 mb-3">
                  <Hash className="h-4 w-4" />
                  Keywords Being Used
                </h4>

                {/* Category Keywords by Language */}
                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground">Category keywords (by language):</p>
                  {Object.entries(categoryDetail.keywords.categoryKeywords).map(([lang, terms]) => (
                    <div key={lang} className="flex items-start gap-2">
                      <Badge variant="outline" className="shrink-0">
                        <Languages className="h-3 w-3 mr-1" />
                        {lang.toUpperCase()}
                      </Badge>
                      <div className="flex flex-wrap gap-1">
                        {terms.map((term, i) => (
                          <Badge key={i} variant="secondary" className="text-xs">
                            {term}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                {categoryDetail.keywords.productKeywords.length > 0 && (
                  <div className="mt-4">
                    <p className="text-sm text-muted-foreground mb-2">
                      Sample product keywords ({categoryDetail.keywords.productKeywords.length} unique):
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {categoryDetail.keywords.productKeywords.slice(0, 10).map((kw, i) => (
                        <Badge key={i} variant="outline" className="text-xs">
                          {kw}
                        </Badge>
                      ))}
                      {categoryDetail.keywords.productKeywords.length > 10 && (
                        <Badge variant="outline" className="text-xs text-muted-foreground">
                          +{categoryDetail.keywords.productKeywords.length - 10} more
                        </Badge>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <Separator />

              {/* Market Breakdown */}
              <div>
                <h4 className="font-semibold flex items-center gap-2 mb-3">
                  <MapPin className="h-4 w-4" />
                  Market Breakdown
                </h4>

                {categoryDetail.marketBreakdown.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No market data available yet</p>
                ) : (
                  <div className="space-y-2">
                    {categoryDetail.marketBreakdown.map((market) => (
                      <div key={market.key} className="rounded-lg border p-3">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline">
                              {market.language.toUpperCase()}
                            </Badge>
                            <span className="font-medium text-sm">{market.location}</span>
                          </div>
                          <span className="text-sm font-mono">
                            {market.valuePercentage}% of value
                          </span>
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-xs text-muted-foreground">
                          <div>
                            <p>Value</p>
                            <p className="text-foreground font-medium">
                              {formatCurrency(market.totalValue, market.currency)}
                            </p>
                          </div>
                          <div>
                            <p>Searches</p>
                            <p className="text-foreground font-medium">
                              {formatNumber(market.totalSearches)}/mo
                            </p>
                          </div>
                          <div>
                            <p>Products</p>
                            <p className="text-foreground font-medium">
                              {market.productCount}
                            </p>
                          </div>
                        </div>
                        {market.categoryTerms.length > 0 && (
                          <div className="mt-2 text-xs">
                            <span className="text-muted-foreground">Keywords: </span>
                            {market.categoryTerms.join(', ')}
                          </div>
                        )}
                        <Progress value={market.valuePercentage} className="h-1 mt-2" />
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <Separator />

              {/* How Data is Computed */}
              <div>
                <h4 className="font-semibold flex items-center gap-2 mb-3">
                  <Info className="h-4 w-4" />
                  How Data is Computed
                </h4>
                <div className="rounded-lg bg-muted/50 p-4 space-y-3">
                  <div>
                    <p className="text-sm font-medium">Formula:</p>
                    <code className="text-xs bg-background px-2 py-1 rounded block mt-1">
                      {categoryDetail.computation.formula}
                    </code>
                  </div>
                  <div>
                    <p className="text-sm font-medium">Data Source:</p>
                    <p className="text-sm text-muted-foreground">{categoryDetail.computation.cpcSource}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium">Steps:</p>
                    <ol className="text-xs text-muted-foreground space-y-1 mt-1 list-decimal list-inside">
                      {categoryDetail.computation.explanation.map((step, i) => (
                        <li key={i}>{step}</li>
                      ))}
                    </ol>
                  </div>
                </div>
              </div>

              <Separator />

              {/* Top Products */}
              <div>
                <h4 className="font-semibold flex items-center gap-2 mb-3">
                  <Package className="h-4 w-4" />
                  Products ({categoryDetail.products.length})
                </h4>
                <ScrollArea className="h-[300px]">
                  <div className="space-y-2">
                    {categoryDetail.products.map((product) => (
                      <div key={product.id} className="rounded-lg border p-3">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate" title={product.name}>
                              {product.name}
                            </p>
                            {product.brand && (
                              <p className="text-xs text-muted-foreground">{product.brand}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            {product.hasContent ? (
                              <CheckCircle2 className="h-4 w-4 text-green-500" />
                            ) : (
                              <XCircle className="h-4 w-4 text-gray-300" />
                            )}
                          </div>
                        </div>
                        <div className="mt-2 flex items-center gap-4 text-xs">
                          <span className="text-muted-foreground">
                            Value: <span className="text-foreground font-medium">
                              {formatCurrency(product.searchValue, currency)}
                            </span>
                          </span>
                          <span className="text-muted-foreground">
                            Searches: <span className="text-foreground font-medium">
                              {formatNumber(product.estimatedSearches)}/mo
                            </span>
                          </span>
                        </div>
                        {product.productKeyword && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Keyword: "{product.productKeyword}"
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
